import type { ReactNode } from 'react';
import { HandCoins, RefreshCw, Undo2, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router';

import type {
  ConvertibleSnapshot,
  FundTransactionDetail,
  FundTransactionType,
  ReconciliationStateResponse,
} from '@/types/fund-transactions';
import { formatCurrency, formatFxRate, formatShares } from '@/utils/formatters';

import { Field } from './transactionDetails';
import { TransactionDetailsCard } from './TransactionDetailsCard';

function humanizeInstrumentType(raw: string): string {
  switch (raw) {
    case 'convertible_note':
      return 'Convertible Note';
    case 'safe_pre':
      return 'SAFE (pre-money)';
    case 'safe_post':
      return 'SAFE (post-money)';
    default:
      return raw;
  }
}

function resolveIcon(transactionType: FundTransactionType) {
  switch (transactionType) {
    case 'CONVERTIBLE_INVESTMENT':
      return <HandCoins className="h-5 w-5" />;
    case 'CONVERTIBLE_CONVERSION':
      return <RefreshCw className="h-5 w-5" />;
    case 'CONVERTIBLE_REPAYMENT':
      return <Undo2 className="h-5 w-5" />;
    default:
      return <HandCoins className="h-5 w-5" />;
  }
}

function resolveIconClassName(transactionType: FundTransactionType): string {
  switch (transactionType) {
    case 'CONVERTIBLE_INVESTMENT':
      return 'bg-emerald-100 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-300';
    case 'CONVERTIBLE_CONVERSION':
      return 'bg-sky-100 text-sky-600 dark:bg-sky-950/40 dark:text-sky-300';
    case 'CONVERTIBLE_REPAYMENT':
      return 'bg-orange-100 text-orange-600 dark:bg-orange-950/40 dark:text-orange-300';
    default:
      return 'bg-emerald-100 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-300';
  }
}

function resolveEyebrow(
  transactionType: FundTransactionType,
  snapshot: ConvertibleSnapshot | null
): string {
  let label: string;
  switch (transactionType) {
    case 'CONVERTIBLE_INVESTMENT':
      label = 'Convertible Investment';
      break;
    case 'CONVERTIBLE_CONVERSION':
      label = 'Convertible Conversion';
      break;
    case 'CONVERTIBLE_REPAYMENT':
      label = 'Convertible Repayment';
      break;
    default:
      label = 'Convertible';
  }
  const companyName = snapshot?.portfolio_company_name;
  return companyName ? `${label} - ${companyName}` : label;
}

function renderConvertibleFields(
  snapshot: ConvertibleSnapshot,
  currency: string,
  transactionAmount: number,
  companyHref: string | null
) {
  return (
    <>
      <Field label="Amount">
        {formatCurrency(transactionAmount, { currency })}
      </Field>
      <Field label="Portfolio Company">
        {companyHref && snapshot.portfolio_company_name ? (
          <Link
            to={companyHref}
            data-testid="view-convertible-link"
            className="inline-flex items-center gap-1 text-sky-600 hover:underline dark:text-sky-400"
          >
            {snapshot.portfolio_company_name}
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        ) : (
          (snapshot.portfolio_company_name ?? '-')
        )}
      </Field>
      <Field label="Instrument type">
        {humanizeInstrumentType(snapshot.instrument_type)}
      </Field>
      {snapshot.principal_amount != null && (
        <Field label="Principal amount">
          {formatCurrency(Number(snapshot.principal_amount), { currency })}
        </Field>
      )}
      {snapshot.accrued_interest != null &&
        Number(snapshot.accrued_interest) !== 0 && (
          <Field label="Accrued interest">
            {formatCurrency(Number(snapshot.accrued_interest), { currency })}
          </Field>
        )}
      {snapshot.shares_issued_on_conversion != null && (
        <Field label="Shares issued on conversion">
          {formatShares(snapshot.shares_issued_on_conversion)}
        </Field>
      )}
      {snapshot.amount_base != null && (
        <Field label="Amount (base)">
          {formatCurrency(Number(snapshot.amount_base), { currency })}
        </Field>
      )}
      {snapshot.fx_rate != null && Number(snapshot.fx_rate) !== 1 && (
        <Field label="FX rate">{formatFxRate(snapshot.fx_rate)}</Field>
      )}
    </>
  );
}

export function ConvertibleDetailsCard({
  transaction,
  fundName,
  reconciliation,
  onViewJournalEntries,
  descriptionAside,
}: {
  transaction: FundTransactionDetail;
  fundName: string;
  reconciliation: ReconciliationStateResponse | undefined;
  onViewJournalEntries?: () => void;
  descriptionAside: ReactNode;
}) {
  const snapshot = transaction.convertible;
  const companyHref =
    snapshot?.portfolio_company_id && transaction.captable_ledger_transaction_id
      ? `/app/portfolio/${snapshot.portfolio_company_id}/transactions/${transaction.captable_ledger_transaction_id}`
      : null;

  return (
    <TransactionDetailsCard
      transaction={transaction}
      fundName={fundName}
      reconciliation={reconciliation}
      onViewJournalEntries={onViewJournalEntries}
      descriptionAside={descriptionAside}
      icon={resolveIcon(transaction.transaction_type)}
      iconClassName={resolveIconClassName(transaction.transaction_type)}
      eyebrow={resolveEyebrow(transaction.transaction_type, snapshot)}
      fields={
        snapshot
          ? renderConvertibleFields(
              snapshot,
              transaction.currency,
              transaction.amount,
              companyHref
            )
          : null
      }
    />
  );
}
